/**
 * DriftBanner — global live indicator at the top of Office.
 *
 * Polls `/api/aria/projects/{id}/drift-status` every 12s. When Aria detects
 * a disagreement between the SHIPPED CODE (`code_truth`) and the
 * documented plan (Revenue Plan / Personas / Business Models), the banner
 * pulses in red and lists the topics in drift (pricing, limits, ICP, …).
 *
 * Click → opens the Execution view so the founder can answer the drift
 * question Aria automatically inserts as the first Loop blocker.
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function DriftBanner({ projectId, onResolve }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch(`${API}/api/aria/projects/${projectId}/drift-status`, {
          credentials: 'include',
        });
        if (!r.ok || cancelled) return;
        setStatus(await r.json());
      } catch { /* silent retry */ }
    };
    tick();
    const t = setInterval(tick, 12000);
    return () => { cancelled = true; clearInterval(t); };
  }, [projectId]);

  if (!status) return null;
  const has = !!status.has_drift;
  const topics = status.topics || [];

  return (
    <div
      data-testid="drift-banner"
      onClick={has ? onResolve : undefined}
      style={{
        margin: '12px 28px 0',
        padding: '10px 16px',
        borderRadius: 12,
        background: has ? '#FEE2E2' : '#ECFDF5',
        border: `1px solid ${has ? '#FCA5A5' : '#A7F3D0'}`,
        display: 'flex', alignItems: 'center', gap: 12,
        cursor: has ? 'pointer' : 'default',
        transition: 'transform 0.15s, box-shadow 0.15s',
        animation: has ? 'driftPulse 2.4s ease-in-out infinite' : 'none',
      }}
      onMouseEnter={e => {
        if (has) {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(220,38,38,0.18)';
        }
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {has
        ? <AlertTriangle size={16} color="#B91C1C" />
        : <ShieldCheck size={16} color="#047857" />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 11, fontWeight: 800, letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: has ? '#7F1D1D' : '#065F46',
        }}>
          {has
            ? `${status.drift_count} drift${status.drift_count > 1 ? 's' : ''} · code ↔ plan disagree`
            : 'Code and plan aligned'}
        </div>
        {has && topics.length > 0 && (
          <div style={{
            fontSize: 11, color: '#7F1D1D',
            marginTop: 2, overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            Topics in drift: {topics.join(' · ')}
          </div>
        )}
      </div>
      {has && (
        <span style={{
          fontSize: 10, fontWeight: 800, letterSpacing: '0.14em',
          color: '#B91C1C', textTransform: 'uppercase',
        }}>
          Resolve →
        </span>
      )}
      <style>{`
        @keyframes driftPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.22); }
          50%      { box-shadow: 0 0 0 6px rgba(220, 38, 38, 0); }
        }
      `}</style>
    </div>
  );
}
